import { ChangeEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    PoolButton,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolTextfield,
} from '@synergy/components-next';
import { PoolMarkdownEditor } from '@synergy/components';
import { postProcessMarkdown } from '@synergy/utils';

import { InvoiceSteps } from '../../../models/invoice-steps';
import { InvoiceTextItem } from '../../../../models/invoice-text-item';
import { ItemsArrayHelper } from './detail-positions-table-container';
import { getHighestId } from '../utils/get-highest-id';
import { getLastPosition } from '../utils/get-last-position';
import { invoiceDetailStepperStateSelector } from '../../../store/invoice-detail-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

interface TextItemModalProps {
    showTextItemModal: boolean;
    onModalClose: () => void;
    helper: ItemsArrayHelper;
    textItemIndex?: number;
    textItem?: InvoiceTextItem;
    onTextItemChange: (textItem?: InvoiceTextItem) => void;
}

export const TextItemModal = (props: TextItemModalProps) => {
    const { showTextItemModal, onModalClose, helper, textItemIndex, textItem, onTextItemChange } = props;
    const readonly = useAppSelector(invoiceDetailStepperStateSelector).readonlySteps.includes(InvoiceSteps.Positions);

    const handleAddOrEditTextItem = () => {
        if (textItemIndex === undefined || !textItem) {
            return;
        }

        if (textItem?.id === -1) {
            const newTextItem = { ...textItem };
            newTextItem.position = getLastPosition(helper) + 1;
            newTextItem.id = getHighestId(helper) + 1;

            helper.push(newTextItem);
        } else {
            helper.replace(textItemIndex, textItem);
        }

        onTextItemChange(undefined);
        onModalClose();
    };

    const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (textItem) {
            onTextItemChange({ ...textItem, title: e.target.value });
        }
    };

    const handleDescriptionChange = (description: string) => {
        if (textItem) {
            onTextItemChange({
                ...textItem,
                description: (description && postProcessMarkdown(description)) || '',
            });
        }
    };

    const isNewTextItem = (textItem?.id ?? -1) < 0;

    return (
        <PoolModal open={showTextItemModal} onClose={onModalClose}>
            <PoolModalTitle>
                <FormattedMessage
                    id={(textItem?.id ?? -1) < 0 ? 'app.invoices.detail.newLine' : 'app.invoices.detail.editLine'}
                />
            </PoolModalTitle>
            <PoolModalContent>
                <PoolTextfield
                    onChange={handleTitleChange}
                    value={textItem?.title}
                    label={<FormattedMessage id="app.label.title" />}
                    fullWidth
                    required={true}
                    sx={{ mb: 2 }}
                />
                <PoolMarkdownEditor
                    value={textItem?.description ?? ''}
                    onChange={handleDescriptionChange}
                    readOnly={readonly}
                />
            </PoolModalContent>
            <PoolModalActions>
                <PoolButton variant="text" onClick={onModalClose}>
                    <FormattedMessage id="app.common.cancel" />
                </PoolButton>
                <PoolButton disabled={!textItem || textItem?.title === ''} onClick={handleAddOrEditTextItem}>
                    <FormattedMessage id={isNewTextItem ? 'app.common.add' : 'app.common.change'} />
                </PoolButton>
            </PoolModalActions>
        </PoolModal>
    );
};
